// src/features/system/orchestrator/components/DeviceApproverPanel.tsx
// ────────────────────────────────────────────────────────────────────────────
// Unified device approval panel — Tier 1 (IoT) + Tier 2 (RFC 8628 smart
// clients) in a single component with sub-tab navigation.
//
// Architecture:
//   Tier 1 — Shelly/ESP32 → BFF /iot/ingest → Orchestrator device-gate
//            unknown devices get a pending scope → admin approves here
//   Tier 2 — PACDaemon / kiosk / FE-peer → POST /oauth/device/code
//            produces ABCD-EFGH user_code → admin approves here
//
// Design: minimalistic dark palette, no redundant frames, DRY sub-components.
// ────────────────────────────────────────────────────────────────────────────
import React, { useMemo, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import MinimalisticTable, { type ColumnDef } from '@/shared/components/ui/MinimalisticTable';
import AppDialog from '@/shared/components/ui/AppDialog';

import {
  fetchPendingIotDevicesApi,
  approveIotDeviceApi,
  rejectIotDeviceApi,
  fetchBlacklistedIotDevicesApi,
  unblockIotDeviceApi,
  type PendingIotDevice,
  type BlacklistedIotDevice,
} from '../api/iotPendingApi';
import {
  fetchPendingDeviceCodesApi,
  approveDeviceCodeApi,
  type PendingDeviceCode,
} from '../api/deviceAuthApi';

// ── Shared query keys ────────────────────────────────────────────────────

const QK_IOT_PENDING  = ['admin', 'orchestrator', 'iot', 'pending']     as const;
const QK_IOT_BLACKLIST = ['admin', 'orchestrator', 'iot', 'blacklist']   as const;
const QK_TIER2_PENDING = ['admin', 'orchestrator', 'device', 'pending'] as const;

// ── Shared helpers ───────────────────────────────────────────────────────

function timeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    const ms = Date.now() - new Date(dateStr).getTime();
    if (ms < 0) return 'just now';
    const sec = Math.floor(ms / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return `${Math.floor(hr / 24)}d ago`;
  } catch { return dateStr; }
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
  } catch { return value; }
}

// ── Reusable status pill ─────────────────────────────────────────────────

function StatusDot({ active, label }: { active: boolean; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-xs">
      {active && (
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
        </span>
      )}
      <span className="text-neutral-400">{label}</span>
    </span>
  );
}

// ── Tier badge ───────────────────────────────────────────────────────────

const TIER_META = {
  iot:   { label: 'Tier 1 · IoT',           accent: 'text-sky-400'    },
  smart: { label: 'Tier 2 · Smart Clients', accent: 'text-violet-400' },
} as const;

type Tier = keyof typeof TIER_META;

function TierBadge({ tier }: { tier: Tier }) {
  const m = TIER_META[tier];
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-widest ${m.accent}`}>
      {m.label}
    </span>
  );
}

// ── Sub-tab bar ──────────────────────────────────────────────────────────

function SubTabs({ active, onChange, counts }: {
  active: Tier;
  onChange: (t: Tier) => void;
  counts: Record<Tier, number>;
}) {
  const tabs: { key: Tier; label: string }[] = [
    { key: 'iot',   label: 'IoT Sensors' },
    { key: 'smart', label: 'Smart Clients' },
  ];

  return (
    <div className="flex border-b border-neutral-800/60">
      {tabs.map(({ key, label }) => {
        const isActive = key === active;
        const count = counts[key];
        return (
          <button
            key={key}
            onClick={() => onChange(key)}
            className={`
              px-4 py-1.5 text-xs font-medium tracking-wide transition-colors
              ${isActive
                ? 'text-neutral-100 border-b-2 border-neutral-100'
                : 'text-neutral-500 hover:text-neutral-300'}
            `}
          >
            {label}
            {count > 0 && (
              <span className={`ml-1.5 inline-flex items-center justify-center px-1.5 min-w-[18px] h-[18px] rounded-full text-[10px] font-bold
                ${isActive ? 'bg-amber-500/20 text-amber-300' : 'pac-glass-subtle text-neutral-400'}
              `}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── TTL countdown ────────────────────────────────────────────────────────

function TtlBadge({ seconds }: { seconds: number }) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  const urgent = seconds <= 60;
  return (
    <span className={`font-mono text-[11px] tabular-nums ${urgent ? 'text-rose-400' : 'text-neutral-500'}`}>
      {m}:{String(s).padStart(2, '0')}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ── MAIN COMPONENT ────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════

export default function DeviceApproverPanel() {
  const qc = useQueryClient();
  const [activeTier, setActiveTier] = useState<Tier>('iot');

  // ── Tier 1 data ────────────────────────────────────────────────────
  const { data: iotPending = [], isLoading: iotLoading, isFetching: iotFetching } =
    useQuery<PendingIotDevice[], Error>({
      queryKey: QK_IOT_PENDING,
      queryFn: fetchPendingIotDevicesApi,
      refetchInterval: 15_000,
    });

  const { data: iotBlacklisted = [], isLoading: blLoading, isFetching: blFetching } =
    useQuery<BlacklistedIotDevice[], Error>({
      queryKey: QK_IOT_BLACKLIST,
      queryFn: fetchBlacklistedIotDevicesApi,
      refetchInterval: 30_000,
    });

  // ── Tier 2 data ────────────────────────────────────────────────────
  const { data: tier2Pending = [], isLoading: t2Loading, isFetching: t2Fetching } =
    useQuery<PendingDeviceCode[], Error>({
      queryKey: QK_TIER2_PENDING,
      queryFn: fetchPendingDeviceCodesApi,
      refetchInterval: 5_000, // device codes are short-lived, poll fast
    });

  // ── Counts for sub-tab badges ──────────────────────────────────────
  const counts: Record<Tier, number> = {
    iot: iotPending.length,
    smart: tier2Pending.length,
  };

  return (
    <div className="flex flex-col gap-3 h-full min-h-0 overflow-auto">
      <SubTabs active={activeTier} onChange={setActiveTier} counts={counts} />

      {activeTier === 'iot' ? (
        <Tier1IotSection
          pending={iotPending}
          isLoading={iotLoading}
          isFetching={iotFetching}
          blacklisted={iotBlacklisted}
          blLoading={blLoading}
          blFetching={blFetching}
          qc={qc}
        />
      ) : (
        <Tier2SmartSection
          pending={tier2Pending}
          isLoading={t2Loading}
          isFetching={t2Fetching}
          blacklisted={iotBlacklisted}
          blLoading={blLoading}
          blFetching={blFetching}
          qc={qc}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ── TIER 1 — IoT Sensors ─────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════

function Tier1IotSection({ pending, isLoading, isFetching, blacklisted, blLoading, blFetching, qc }: {
  pending: PendingIotDevice[];
  isLoading: boolean;
  isFetching: boolean;
  blacklisted: BlacklistedIotDevice[];
  blLoading: boolean;
  blFetching: boolean;
  qc: ReturnType<typeof useQueryClient>;
}) {
  // ── Approve dialog ─────────────────────────────────────────────────
  const [approveTarget, setApproveTarget] = useState<PendingIotDevice | null>(null);
  const [approveLabel, setApproveLabel] = useState('');

  const openApprove = useCallback((device: PendingIotDevice) => {
    setApproveTarget(device);
    setApproveLabel(device.device_id);
  }, []);

  const closeApprove = useCallback(() => {
    setApproveTarget(null);
    setApproveLabel('');
  }, []);

  // ── Mutations ──────────────────────────────────────────────────────
  const approveMut = useMutation({
    mutationFn: () => approveIotDeviceApi(approveTarget!.device_id, approveLabel.trim() || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_IOT_PENDING });
      qc.invalidateQueries({ queryKey: ['admin', 'orchestrator', 'devices'] });
      closeApprove();
    },
  });

  const rejectMut = useMutation({
    mutationFn: (deviceId: string) => rejectIotDeviceApi(deviceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_IOT_PENDING });
      qc.invalidateQueries({ queryKey: QK_IOT_BLACKLIST });
    },
  });

  const unblockMut = useMutation({
    mutationFn: (deviceId: string) => unblockIotDeviceApi(deviceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_IOT_BLACKLIST });
      qc.invalidateQueries({ queryKey: QK_IOT_PENDING });
    },
  });

  // ── Table columns ──────────────────────────────────────────────────
  const columns: ColumnDef<PendingIotDevice>[] = useMemo(() => [
    {
      header: 'Device ID',
      accessor: 'device_id',
      Cell: (v: string) => <span className="font-mono text-amber-300 text-[13px]">{v || '—'}</span>,
    },
    {
      header: 'First Seen',
      accessor: 'first_seen',
      Cell: (v: string | null) => (
        <span className="text-neutral-400 text-xs" title={formatDate(v)}>{timeAgo(v)}</span>
      ),
    },
    {
      header: '',
      accessor: 'device_id' as keyof PendingIotDevice,
      sortable: false,
      Cell: (_v: string, row: PendingIotDevice) => (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); openApprove(row); }}
            className="px-2.5 py-1 rounded text-xs font-medium bg-emerald-600/20 text-emerald-400 border border-emerald-700/40 hover:bg-emerald-600/30 transition-colors"
          >
            Approve
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`Reject & blacklist "${row.device_id}"?`)) {
                rejectMut.mutate(row.device_id);
              }
            }}
            disabled={rejectMut.isPending}
            className="px-2.5 py-1 rounded text-xs font-medium pac-glass-btn text-neutral-400 hover:text-rose-400 hover:border-rose-700/40 transition-colors disabled:opacity-50"
          >
            Reject
          </button>
        </div>
      ),
    },
  ], [openApprove, rejectMut]);

  const blacklistCols: ColumnDef<BlacklistedIotDevice>[] = useMemo(() => [
    {
      header: 'Device ID',
      accessor: 'device_id',
      Cell: (v: string) => <span className="font-mono text-rose-400 text-[13px]">{v || '—'}</span>,
    },
    {
      header: 'Rejected',
      accessor: 'first_seen',
      Cell: (v: string | null) => (
        <span className="text-neutral-400 text-xs" title={formatDate(v)}>{timeAgo(v)}</span>
      ),
    },
    {
      header: '',
      accessor: 'device_id' as keyof BlacklistedIotDevice,
      sortable: false,
      Cell: (_v: string, row: BlacklistedIotDevice) => (
        <div className="flex items-center justify-end">
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`Unblock "${row.device_id}"?`)) unblockMut.mutate(row.device_id);
            }}
            disabled={unblockMut.isPending}
            className="px-2.5 py-1 rounded text-xs font-medium pac-glass-btn text-neutral-400 hover:text-sky-400 hover:border-sky-700/40 transition-colors disabled:opacity-50"
          >
            Unblock
          </button>
        </div>
      ),
    },
  ], [unblockMut]);

  const hasPending = pending.length > 0;
  const hasBlacklisted = blacklisted.length > 0;

  return (
    <>
      {/* Approve dialog */}
      <AppDialog
        open={!!approveTarget}
        onOpenChange={(open) => { if (!open) closeApprove(); }}
        title="Approve IoT device"
        description={approveTarget ? `Register "${approveTarget.device_id}" as an active device. Future ingest data will flow to hot_stream.` : undefined}
        size="sm"
        onConfirm={() => approveMut.mutate()}
        confirmLabel={approveMut.isPending ? 'Approving…' : 'Approve'}
        confirmDisabled={approveMut.isPending}
        isBusy={approveMut.isPending}
      >
        <div className="space-y-3">
          {approveMut.error && (
            <p className="text-sm text-rose-400">{(approveMut.error as Error)?.message || 'Approval failed'}</p>
          )}
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-neutral-400">Label (optional)</label>
            <input
              type="text"
              value={approveLabel}
              onChange={(e) => setApproveLabel(e.target.value)}
              placeholder={approveTarget?.device_id ?? 'Device label'}
              className="bg-black border border-neutral-700 rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
          {approveTarget?.first_seen && (
            <p className="text-xs text-neutral-500">First seen: {formatDate(approveTarget.first_seen)}</p>
          )}
        </div>
      </AppDialog>

      {/* Pending section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <TierBadge tier="iot" />
            <StatusDot active={hasPending} label={
              isLoading ? 'Loading…' :
              hasPending ? `${pending.length} pending` : 'No pending devices'
            } />
            {isFetching && !isLoading && (
              <span className="text-[10px] text-neutral-600 uppercase tracking-wider">refreshing</span>
            )}
          </div>
        </div>

        {!isLoading && !hasPending && (
          <EmptyState
            message="When an unknown IoT device hits the ingest endpoint, it will appear here for approval."
            hint="Approved devices start recording telemetry to hot_stream automatically."
          />
        )}

        {!isLoading && hasPending && (
          <TableFrame>
            <MinimalisticTable<PendingIotDevice>
              columns={columns}
              data={pending}
              noDataMessage="No pending devices."
              showSearch
              searchPlaceholder="Filter pending…"
            />
          </TableFrame>
        )}
      </div>

      {/* Blacklisted section */}
      {(hasBlacklisted || blLoading) && (
        <div className="space-y-3 mt-4">
          <div className="flex items-center gap-2.5">
            <span className="text-xs text-neutral-500">
              {blLoading ? 'Loading…' : `${blacklisted.length} blacklisted`}
            </span>
            {blFetching && !blLoading && (
              <span className="text-[10px] text-neutral-600 uppercase tracking-wider">refreshing</span>
            )}
          </div>
          {hasBlacklisted && (
            <TableFrame>
              <MinimalisticTable<BlacklistedIotDevice>
                columns={blacklistCols}
                data={blacklisted}
                noDataMessage="No blacklisted devices."
                showSearch
                searchPlaceholder="Filter blacklisted…"
              />
            </TableFrame>
          )}
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ── TIER 2 — Smart Clients (RFC 8628) ────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════

function Tier2SmartSection({ pending, isLoading, isFetching, blacklisted, blLoading, blFetching, qc }: {
  pending: PendingDeviceCode[];
  isLoading: boolean;
  isFetching: boolean;
  blacklisted: BlacklistedIotDevice[];
  blLoading: boolean;
  blFetching: boolean;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const [confirmTarget, setConfirmTarget] = useState<PendingDeviceCode | null>(null);

  const approveMut = useMutation({
    mutationFn: (userCode: string) => approveDeviceCodeApi(userCode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_TIER2_PENDING });
      qc.invalidateQueries({ queryKey: ['admin', 'orchestrator', 'devices'] });
      setConfirmTarget(null);
    },
  });

  const unblockMut = useMutation({
    mutationFn: (deviceId: string) => unblockIotDeviceApi(deviceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_IOT_BLACKLIST });
      qc.invalidateQueries({ queryKey: QK_TIER2_PENDING });
    },
  });

  const columns: ColumnDef<PendingDeviceCode>[] = useMemo(() => [
    {
      header: 'Code',
      accessor: 'user_code',
      Cell: (v: string) => (
        <span className="font-mono text-lg tracking-[0.2em] text-violet-300 font-bold select-all">{v}</span>
      ),
    },
    {
      header: 'Device',
      accessor: 'device_id',
      Cell: (v: string) => (
        <span className="font-mono text-neutral-300 text-[13px]">{v || '—'}</span>
      ),
    },
    {
      header: 'Scope',
      accessor: 'scope',
      Cell: (v: string[]) => (
        <div className="flex flex-wrap gap-1">
          {(v || []).map((s) => (
            <span key={s} className="px-1.5 py-0.5 rounded pac-glass-subtle text-neutral-400 text-[11px] font-mono">{s}</span>
          ))}
        </div>
      ),
    },
    {
      header: 'TTL',
      accessor: 'ttl_sec',
      Cell: (v: number) => <TtlBadge seconds={v} />,
    },
    {
      header: 'Requested',
      accessor: 'issued_at',
      Cell: (v: string) => (
        <span className="text-neutral-500 text-xs" title={formatDate(v)}>{timeAgo(v)}</span>
      ),
    },
    {
      header: '',
      accessor: 'user_code' as keyof PendingDeviceCode,
      sortable: false,
      Cell: (_v: string, row: PendingDeviceCode) => (
        <div className="flex items-center justify-end">
          <button
            onClick={(e) => { e.stopPropagation(); setConfirmTarget(row); }}
            className="px-2.5 py-1 rounded text-xs font-medium bg-emerald-600/20 text-emerald-400 border border-emerald-700/40 hover:bg-emerald-600/30 transition-colors"
          >
            Authorize
          </button>
        </div>
      ),
    },
  ], []);

  const hasPending = pending.length > 0;
  const hasBlacklisted = blacklisted.length > 0;

  const blacklistCols: ColumnDef<BlacklistedIotDevice>[] = useMemo(() => [
    {
      header: 'Device ID',
      accessor: 'device_id',
      Cell: (v: string) => <span className="font-mono text-rose-400 text-[13px]">{v || '—'}</span>,
    },
    {
      header: 'Blacklisted',
      accessor: 'first_seen',
      Cell: (v: string | null) => (
        <span className="text-neutral-400 text-xs" title={formatDate(v)}>{timeAgo(v)}</span>
      ),
    },
    {
      header: '',
      accessor: 'device_id' as keyof BlacklistedIotDevice,
      sortable: false,
      Cell: (_v: string, row: BlacklistedIotDevice) => (
        <div className="flex items-center justify-end">
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`Unblock "${row.device_id}"?`)) unblockMut.mutate(row.device_id);
            }}
            disabled={unblockMut.isPending}
            className="px-2.5 py-1 rounded text-xs font-medium pac-glass-btn text-neutral-400 hover:text-sky-400 hover:border-sky-700/40 transition-colors disabled:opacity-50"
          >
            Unblock
          </button>
        </div>
      ),
    },
  ], [unblockMut]);

  return (
    <>
      {/* Confirm dialog */}
      <AppDialog
        open={!!confirmTarget}
        onOpenChange={(open) => { if (!open) setConfirmTarget(null); }}
        title="Authorize Device"
        size="sm"
        onConfirm={() => { if (confirmTarget) approveMut.mutate(confirmTarget.user_code); }}
        confirmLabel={approveMut.isPending ? 'Authorizing…' : 'Authorize'}
        confirmDisabled={approveMut.isPending}
        isBusy={approveMut.isPending}
      >
        {confirmTarget && (
          <div className="space-y-4">
            {approveMut.error && (
              <p className="text-sm text-rose-400 bg-rose-950/30 border border-rose-900/40 rounded px-3 py-2">
                {(approveMut.error as Error)?.message || 'Authorization failed'}
              </p>
            )}

            {/* Prominent code display */}
            <div className="flex flex-col items-center py-4">
              <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-600 mb-2">Pairing Code</span>
              <span className="font-mono text-2xl font-bold tracking-[0.2em] text-emerald-400">
                {confirmTarget.user_code}
              </span>
            </div>

            <div className="h-px bg-white/5/60" />

            {/* Metadata card */}
            <div className="rounded border border-neutral-800/50 bg-black/30 px-4 py-3 space-y-2.5">
              <InfoRow label="Device" value={confirmTarget.device_id} mono />
              <InfoRow label="Scope" value={(confirmTarget.scope || []).join(', ')} />
              <InfoRow label="Expires" value={formatDate(confirmTarget.expires_at)} />
            </div>

            <p className="text-[11px] text-neutral-600 text-center leading-relaxed">
              Grants a scoped JWT for{' '}
              <span className="text-neutral-400 font-medium">{(confirmTarget.scope || []).join(', ')}</span>{' '}
              operations on this environment.
            </p>
          </div>
        )}
      </AppDialog>

      {/* Pending section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <TierBadge tier="smart" />
            <StatusDot active={hasPending} label={
              isLoading ? 'Loading…' :
              hasPending ? `${pending.length} pending` : 'No pending device codes'
            } />
            {isFetching && !isLoading && (
              <span className="text-[10px] text-neutral-600 uppercase tracking-wider">refreshing</span>
            )}
          </div>
        </div>

        {!isLoading && !hasPending && (
          <EmptyState
            message="When a Tier 2 client (PACDaemon, kiosk, peer FE) initiates an RFC 8628 device authorization flow, the pending code will appear here."
            hint="Codes expire after a few minutes. The client polls until you approve."
          />
        )}

        {!isLoading && hasPending && (
          <TableFrame>
            <MinimalisticTable<PendingDeviceCode>
              columns={columns}
              data={pending}
              noDataMessage="No pending device codes."
              showSearch
              searchPlaceholder="Filter codes…"
            />
          </TableFrame>
        )}
      </div>

      {/* Blacklisted section */}
      {(hasBlacklisted || blLoading) && (
        <div className="space-y-3 mt-4">
          <div className="flex items-center gap-2.5">
            <span className="text-xs text-neutral-500">
              {blLoading ? 'Loading…' : `${blacklisted.length} blacklisted`}
            </span>
            {blFetching && !blLoading && (
              <span className="text-[10px] text-neutral-600 uppercase tracking-wider">refreshing</span>
            )}
          </div>
          {hasBlacklisted && (
            <TableFrame>
              <MinimalisticTable<BlacklistedIotDevice>
                columns={blacklistCols}
                data={blacklisted}
                noDataMessage="No blacklisted devices."
                showSearch
                searchPlaceholder="Filter blacklisted…"
              />
            </TableFrame>
          )}
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ── DRY shared micro-components ──────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════

function EmptyState({ message, hint }: { message: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-dashed border-neutral-800 pac-glass p-6 text-center">
      <p className="text-sm text-neutral-500">{message}</p>
      {hint && <p className="mt-1 text-xs text-neutral-600">{hint}</p>}
    </div>
  );
}

function TableFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="pac-glass rounded-md p-2">
      {children}
    </div>
  );
}

function InfoRow({ label, value, mono, accent }: {
  label: string;
  value: string;
  mono?: boolean;
  accent?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-xs uppercase tracking-wide text-neutral-500 w-16 shrink-0">{label}</span>
      <span className={`text-sm ${mono ? 'font-mono' : ''} ${accent ? 'text-violet-300 tracking-[0.15em] font-bold' : 'text-neutral-300'}`}>
        {value || '—'}
      </span>
    </div>
  );
}
